import FiltersBadge from '@/components/Filter/Badge';
import { setFilters } from '@/store/slices/forecasterSlice';
import { Button, FormLabel, Popover, backdropClasses, popoverClasses, styled } from '@mui/material';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import MultiSelect from '../../UserManagement/Components/MultiSelect';

const StyledPopover = styled(Popover)({
  [`& .${popoverClasses.paper}`]: {
    minWidth: 344,
    maxWidth: 344,
    padding: '16px 20px',
    borderRadius: '8px',
    backgroundColor: 'white',
    border: 'none',
    boxShadow: '0 10px 10px 0 rgb(16 10 85 / .1)',
  },
  [`& .${backdropClasses.root}`]: {
    backgroundColor: 'rgb(0 0 0 / .4)',
  },
});

const ActionButton = styled(Button)({
  flex: 1,
  height: '40px',
  fontWeight: 600,
  boxShadow: 'none',
});

const ClearAllButton = styled(Button)({
  borderBottom: '1px solid #1B4332',
  padding: 0,
  borderRadius: 0,
  lineHeight: 1,
  minWidth: 0,
});

const FiltersPopover = ({ id, open, anchorEl, handleClose, filtersBadgeCount }) => {
  const { filters } = useSelector(state => state.forecaster);

  // TODO: move constants out of component into a separate file
  const channelsList = [
    {
      value: 1,
      label: 'Display',
    },
    {
      value: 2,
      label: 'OLV',
    },
    {
      value: 3,
      label: 'Streaming TV',
    },
    {
      value: 4,
      label: 'Audio',
    },
    {
      value: 5,
      label: 'Digital Out of Home',
    },
  ];
  const packagesList = [
    {
      value: 1,
      label: 'RON',
    },
    {
      value: 2,
      label: 'Gold',
    },
    {
      value: 3,
      label: 'Silver',
    },
    {
      value: 4,
      label: 'Bronze',
    },
    {
      value: 5,
      label: 'News Only',
    },
    {
      value: 6,
      label: 'Sports',
    },
    {
      value: 7,
      label: 'Spanish Language',
    },
  ];
  const dispatch = useDispatch();
  const setFilter = useCallback(
    ({ channel: channelFilter, packages: packagesFilter }) =>
      dispatch(
        setFilters({
          channel: channelFilter,
          packages: packagesFilter,
        }),
      ),
    [dispatch],
  );

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      channel: filters?.channel || [],
      packages: filters?.packages || [],
    },
  });

  const onClearAll = () => {
    const resetValues = { channel: [], packages: [] };
    reset(resetValues);
    setFilter(resetValues);
    handleClose();
  };

  const onSubmit = data => {
    setFilter({ ...data });
    handleClose();
  };

  const onReset = e => {
    e?.preventDefault?.();
    reset({
      channel: filters?.channel || [],
      packages: filters?.packages || [],
    });
    handleClose();
  };

  return (
    <StyledPopover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <form onReset={onReset} onSubmit={handleSubmit(onSubmit)}>
        <div className='flex justify-between'>
          <div className='flex gap-[9px] items-center'>
            <span className='text-[18px] color-[#2E2C34] font-semibold'>Filter</span>
            <FiltersBadge count={filtersBadgeCount} sx={{ width: 23, height: 23 }} />
          </div>
          <div>
            <ClearAllButton disableRipple type='button' onClick={onClearAll}>
              Clear all
            </ClearAllButton>
          </div>
        </div>
        <hr className='-mx-[32px] my-[10px]' />
        <div className='my-[10px]'>
          <FormLabel htmlFor='channel-filter-select'>Channel</FormLabel>
          <Controller
            name='channel'
            defaultValue={[]}
            control={control}
            render={({ field }) => <MultiSelect {...field} triggerId='client-filter-select' options={channelsList} />}
          />
        </div>
        <hr className='-mx-[32px]' />
        <div className='my-[10px]'>
          <FormLabel htmlFor='packages-filter-select'>Packages</FormLabel>
          <Controller
            name='packages'
            defaultValue={[]}
            control={control}
            render={({ field }) => (
              <MultiSelect {...field} triggerId='advertiser-filter-select' options={packagesList} />
            )}
          />
        </div>
        <hr className='-mx-[32px] mb-[16px]' />
        <div className='flex gap-[12px]'>
          <ActionButton variant='outlined' type='reset'>
            Cancel
          </ActionButton>
          <ActionButton variant='contained' type='submit'>
            Apply
          </ActionButton>
        </div>
      </form>
    </StyledPopover>
  );
};

FiltersPopover.propTypes = {
  id: PropTypes.string,
  open: PropTypes.bool,
  anchorEl: PropTypes.object,
  handleClose: PropTypes.func,
  onApplyFilters: PropTypes.func,
  channel: PropTypes.array,
  filtersBadgeCount: PropTypes.number,
};

export default FiltersPopover;
